import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

function cartDeliveryDate(now, london) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', hourCycle: 'h23',
  }).formatToParts(now);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  const today = new Date(Date.UTC(Number(values.year), Number(values.month) - 1, Number(values.day), 12));
  const day = today.getUTCDay();
  const hour = Number(values.hour);
  const following = (allowed, offset) => {
    for (let index = offset; index < 15; index++) {
      const date = new Date(today);
      date.setUTCDate(date.getUTCDate() + index);
      if (allowed.includes(date.getUTCDay())) return date;
    }
  };
  if (london) return following([1, 5], 1);
  let collection;
  if (day === 1) collection = hour >= 18 ? following([4], 3) : following([2], 1);
  else if (day === 2 || day === 3) collection = following([4], day === 3 ? 1 : 2);
  else if (day === 4) collection = following([5], 1);
  else if (day === 5) collection = following([1], 3);
  else if (day === 6) collection = following([1], 2);
  else collection = following([1], 1);
  const arrival = new Date(collection);
  do { arrival.setUTCDate(arrival.getUTCDate() + 1); } while ([0, 6].includes(arrival.getUTCDay()));
  return arrival;
}

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener('click', this.#handleUpsellClick);
    this.addEventListener('change', this.#handleUpsellVariant);
    this.#extrasObserver.observe(this, { childList: true, subtree: true });
    this.#refreshExtras();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener('click', this.#handleUpsellClick);
    this.removeEventListener('change', this.#handleUpsellVariant);
    this.#extrasObserver.disconnect();
  }



  #extrasObserver = new MutationObserver(() => this.#refreshExtras());

  #refreshExtras = (rotate = false) => {
    const track = this.querySelector('.pw-plant-cross-sells__items');
    if (track && (rotate || track.dataset.pwReady !== 'true')) {
      track.dataset.pwReady = 'true';
      const cards = [...track.querySelectorAll('.pw-plant-cross-sell')];
      for (let index = cards.length - 1; index > 0; index--) {
        const other = Math.floor(Math.random() * (index + 1));
        [cards[index], cards[other]] = [cards[other], cards[index]];
      }
      let previousFirst;
      try { previousFirst = sessionStorage.getItem('pw-cart-first-plant'); } catch {}
      if (cards.length > 1 && cards[0].dataset.pwProductId === previousFirst) cards.push(cards.shift());
      cards.forEach((card, index) => {
        card.hidden = index >= 10;
        track.append(card);
      });
      track.scrollLeft = 0;
      try {
        if (cards[0]) sessionStorage.setItem('pw-cart-first-plant', cards[0].dataset.pwProductId);
      } catch {}
    }
    for (const label of this.querySelectorAll('[data-pw-cart-eta]')) {
      const date = cartDeliveryDate(new Date(), label.dataset.london === 'true');
      const text = new Intl.DateTimeFormat('en-GB', {
        weekday: 'long', day: 'numeric', month: 'short', timeZone: 'UTC',
      }).format(date);
      if (label.textContent !== text) label.textContent = text;
    }
  };

  #upsellPending = false;

  #handleUpsellVariant = (event) => {
    if (!(event.target instanceof HTMLSelectElement) || !event.target.matches('[data-pw-variant]')) return;
    const card = event.target.closest('.pw-plant-cross-sell');
    const option = event.target.selectedOptions[0];
    if (!card || !option) return;
    const photo = card.querySelector('[data-pw-photo]');
    const price = card.querySelector('.pw-plant-cross-sell__price');
    if (photo instanceof HTMLImageElement && option.dataset.photo) photo.src = option.dataset.photo;
    if (price && option.dataset.price) price.textContent = option.dataset.price;
    const badge = card.querySelector('[data-pw-unlock-badge]');
    if (badge instanceof HTMLElement) badge.hidden = option.dataset.pwUnlocks !== 'true';
  };

  #handleUpsellClick = async (event) => {
    if (!(event.target instanceof Element)) return;
    const arrow = event.target.closest('.pw-cross-next');
    if (arrow) {
      const track = arrow.closest('.pw-plant-cross-sells')?.querySelector('.pw-plant-cross-sells__items');
      const card = track?.querySelector('.pw-plant-cross-sell');
      if (!track || !card) return;
      const step = card.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || '0');
      const left = track.scrollLeft + track.clientWidth >= track.scrollWidth - 5 ? 0 : track.scrollLeft + step;
      track.scrollTo({ left, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      return;
    }
    const button = event.target.closest('[data-pw-add]');
    if (!(button instanceof HTMLButtonElement) || this.#upsellPending) return;
    const row = button.closest('.pw-plant-cross-sell');
    const select = row?.querySelector('[data-pw-variant]');
    const cartItems = this.querySelector('cart-items-component');
    if (!(select instanceof HTMLSelectElement) || !cartItems?.dataset.sectionId) return;
    event.preventDefault();
    event.stopPropagation();
    this.#upsellPending = true;
    const initialLabel = button.innerHTML;
    const errorLabel = this.querySelector('[data-pw-upsell-error]');
    if (errorLabel instanceof HTMLElement) errorLabel.hidden = true;
    button.disabled = true;
    button.textContent = 'Adding…';
    cartItems.classList.add('cart-items-disabled');
    let added = false;
    try {
      const sectionIds = [...new Set(
        [...document.querySelectorAll('cart-items-component[data-section-id]')].map((item) => item.dataset.sectionId)
      )].slice(0, 5);
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(select.value), quantity: 1 }],
          sections: sectionIds,
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || 'Unable to add this plant.');
      added = true;
      const sectionHtml = data.sections?.[cartItems.dataset.sectionId];
      const sectionDoc = new DOMParser().parseFromString(sectionHtml || '', 'text/html');
      let itemCount = Number(sectionDoc.querySelector('[ref="cartItemCount"]')?.textContent?.trim());
      if (!sectionHtml || !Number.isFinite(itemCount) || itemCount < 1) {
        const cartResponse = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } });
        if (!cartResponse.ok) throw new Error('Plant added. Please refresh your basket to see it.');
        const cart = await cartResponse.json();
        itemCount = cart.item_count;
      }
      this.dispatchEvent(new CartAddEvent(data, this.id, {
        source: 'pw-cart-upsell',
        itemCount,
        sections: data.sections || {},
      }));
      button.textContent = 'Added';
    } catch (error) {
      if (errorLabel instanceof HTMLElement) {
        errorLabel.textContent = added ? 'Plant added. Please refresh your basket to see it.' :
          error instanceof Error ? error.message : 'Unable to add this plant. Please try again.';
        errorLabel.hidden = false;
      }
      if (!added) {
        button.disabled = false;
        button.innerHTML = initialLabel;
      } else {
        button.textContent = 'Added';
      }
    } finally {
      cartItems.classList.remove('cart-items-disabled');
      this.#upsellPending = false;
    }
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.#refreshExtras(true);
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
